// src/context/OnboardingOverlayContext.tsx
//
// H-2C2 Phase 2.B — Step 1 (inert).
//
// React Context that owns the `dismissedThisSession` flag for the
// onboarding overlay. NOT Redux — the flag is purely UI-local and
// per-session; a slice for a single boolean would be over-engineering.
//
// Lifetime:
//   - The provider is mounted inside <AppLayout> in a later Phase 2.B PR
//     (not this PR — this PR is inert).
//   - State resets on every provider mount, which equates to "per
//     authenticated session" (AppLayout sits behind <RequireAuth>; a
//     fresh page load = fresh mount = fresh state).
//   - NEVER persisted to localStorage / sessionStorage. Incomplete users
//     get another opportunity to see the overlay on next session.

import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react';

// ----------------------------------------------------------------------
// Public types
// ----------------------------------------------------------------------

export interface OnboardingOverlayContextValue {
  /** User has explicitly dismissed the overlay this session. */
  dismissedThisSession: boolean;
  /** Mark the overlay as dismissed for the remainder of this session. */
  setDismissed: () => void;
  /** Re-enable the overlay (e.g. from a "Resume setup" entry in Settings). */
  clearDismissed: () => void;
}

// ----------------------------------------------------------------------
// Internals
// ----------------------------------------------------------------------

// `null` default so consumers that forget the provider get a clear error
// (see `useOnboardingOverlayContext` below) instead of a silently-wrong
// behavior.
const OnboardingOverlayContext = createContext<
  OnboardingOverlayContextValue | null
>(null);

// ----------------------------------------------------------------------
// Public API
// ----------------------------------------------------------------------

/**
 * Wraps the subtree that may render the onboarding overlay (typically the
 * authenticated app layout). State is per-mount; remount = fresh state.
 *
 * In Phase 2.B step 1 (this PR) the provider is NOT mounted in
 * <AppLayout>. Subsequent PRs will mount it.
 */
export function OnboardingOverlayProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [dismissedThisSession, setState] = useState(false);

  const setDismissed = useCallback(() => {
    setState(true);
  }, []);

  const clearDismissed = useCallback(() => {
    setState(false);
  }, []);

  const value: OnboardingOverlayContextValue = {
    dismissedThisSession,
    setDismissed,
    clearDismissed,
  };

  return (
    <OnboardingOverlayContext.Provider value={value}>
      {children}
    </OnboardingOverlayContext.Provider>
  );
}

/**
 * Read the overlay context. Throws if called outside an
 * <OnboardingOverlayProvider> — that error is what the test suite asserts
 * to lock in the "no silent wrong behavior" contract.
 */
export function useOnboardingOverlayContext(): OnboardingOverlayContextValue {
  const ctx = useContext(OnboardingOverlayContext);
  if (!ctx) {
    throw new Error(
      'useOnboardingOverlayContext must be used inside <OnboardingOverlayProvider>',
    );
  }
  return ctx;
}
